/**
 * Ad Provider — page-level orchestration.
 *
 * Reads the server-injected ad config, selects the correct provider strategy
 * (Freestar or self-managed GPT + header bidding), and exposes a slot
 * registration API for the <ad-slot> web component.
 *
 * Consent gating (OneTrust) hooks are stubbed and commented out.
 */

import { FreestarProvider } from "./providers/freestar.js";
import { SelfManagedProvider } from "./providers/self-managed.js";

/**
 * Read the server-injected ad config from the page.
 * @returns {object|null}
 */
function readAdConfig() {
  const el = document.getElementById("ad-config");
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    console.error("[ads] Failed to parse ad config");
    return null;
  }
}

/**
 * Initialise ads. Called once on page load.
 */
export function initializeAds() {
  const config = readAdConfig();
  if (!config) return; // no ads on this page (premium user, or no config)

  // --- OneTrust consent gate (uncomment when CMP is integrated) ---
  // import('./consent.js').then(({ waitForConsent }) => {
  //   waitForConsent().then((consent) => {
  //     config.consent = consent;
  //     createProvider(config);
  //   });
  // });

  // --- Without consent gate, initialise immediately ---
  createProvider(config);
}

/**
 * Instantiate the correct provider and expose it globally.
 * @param {object} config
 */
function createProvider(config) {
  const provider =
    config.provider === "freestar"
      ? new FreestarProvider(config)
      : new SelfManagedProvider(config);

  globalThis.__adProvider = provider;
  provider.initialize();

  // Notify any <ad-slot> elements that connected before the provider was ready
  globalThis.dispatchEvent(new CustomEvent("ad-provider:ready"));
}
